import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Typography, Avatar, Chip, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatCurrency } from '../../utils/formatters';
import { getCourses, deleteCourse } from '../../api/endpoints/courses';

const CourseList = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (catFilter !== 'all') params.category = catFilter;
      const res = await getCourses(params);
      setCourses(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, catFilter]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const handleDelete = async () => {
    try {
      await deleteCourse(deleteId);
      toast.success('Course deleted');
      setDeleteId(null);
      fetchCourses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete course');
    }
  };

  const categories = [...new Set(courses.map((c) => c.category))];

  const columns = [
    { id: 'title', label: 'Course', render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.title}</Typography>
        <Typography variant="caption" color="text.secondary">{row.instructorId?.name || row.instructor || '—'}</Typography>
      </Box>
    )},
    { id: 'category', label: 'Category' },
    { id: 'price', label: 'Price', align: 'right', render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={600}>{formatCurrency(row.price)}</Typography>
        {row.comparePrice > row.price && <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>{formatCurrency(row.comparePrice)}</Typography>}
      </Box>
    )},
    { id: 'totalStudents', label: 'Students', align: 'center' },
    { id: 'totalLessons', label: 'Lessons', align: 'center' },
    { id: 'isPublished', label: 'Status', render: (row) => <StatusChip status={row.isPublished ? 'published' : 'draft'} /> },
    { id: 'actions', label: '', render: (row) => (
      <Stack direction="row">
        <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/courses/${row._id}/edit`)}><EditIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(row._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Courses" subtitle="Manage astrology courses" action={{ label: 'Add Course', onClick: () => navigate('/courses/new') }} />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <SearchBar value={search} onChange={setSearch} placeholder="Search courses..." sx={{ minWidth: 300 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Category</InputLabel>
          <Select value={catFilter} label="Category" onChange={(e) => { setCatFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataTable columns={columns} rows={courses} page={page} rowsPerPage={rowsPerPage} total={total}
          onPageChange={setPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} loading={loading} />
      )}
      <ConfirmDialog open={!!deleteId} title="Delete Course" message="Are you sure?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </Box>
  );
};

export default CourseList;
